import React, { useRef, useState } from 'react';
import ChartRenderer from './ChartRenderer';
import styles from './ReportPreviewSection.module.css';

export default function ReportPreviewSection({
    reportData,
    selectedMetrics,
    chartTypes,
    protestsCount,
    onSave,
    onBack,
    saving,
}) {
    const reportRef = useRef(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const handleDownloadPDF = async () => {
        setPdfLoading(true);
        try {
            const html2pdf = (await import('html2pdf.js')).default;
            const element = reportRef.current;
            const options = {
                margin: 10,
                filename: 'تقرير-الاحتجاجات.pdf',
                image: { type: 'jpeg', quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { orientation: 'landscape', unit: 'mm', format: 'a4' },
            };
            await html2pdf().set(options).from(element).save();
        } catch {
            alert('فشل تحميل PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const verifiedData = reportData.verified_ratio
        ? [
              { name: 'مرصود', count: reportData.verified_ratio.verified },
              { name: 'غير مرصود', count: reportData.verified_ratio.unverified },
          ]
        : [];

    return (
        <div className={styles.section}>
            <div className={styles.toolbar}>
                {onBack && <button className={styles.backBtn} onClick={onBack}>← السابق</button>}
                <span className={styles.countBadge}>
                    {protestsCount?.toLocaleString('ar-SY')} احتجاج مشمول
                </span>
                <div className={styles.toolbarRight}>
                    <button className={styles.printBtn} onClick={() => window.print()}>
                        🖨 طباعة
                    </button>
                    <button
                        className={styles.pdfBtn}
                        onClick={handleDownloadPDF}
                        disabled={pdfLoading}
                    >
                        {pdfLoading ? '...' : '⬇ PDF'}
                    </button>
                    {onSave && (
                        <button
                            className={styles.saveBtn}
                            onClick={onSave}
                            disabled={saving}
                        >
                            {saving ? 'جاري الحفظ...' : '💾 حفظ التقرير'}
                        </button>
                    )}
                </div>
            </div>

            {/* Printable report content */}
            <div ref={reportRef} className={styles.report}>
                <div className={styles.reportHeader}>
                    <h1>تقرير الاحتجاجات</h1>
                    <p className={styles.reportDate}>
                        تاريخ الإنشاء: {new Date().toLocaleDateString('ar-SY', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                </div>

                {/* Basic stats row */}
                {(selectedMetrics.total_protests || selectedMetrics.total_participants || selectedMetrics.avg_participants) && (
                    <div className={styles.statsRow}>
                        {selectedMetrics.total_protests && reportData.total_protests != null && (
                            <div className={styles.statCard}>
                                <span className={styles.statValue}>{reportData.total_protests?.toLocaleString('ar-SY')}</span>
                                <span className={styles.statLabel}>إجمالي الاحتجاجات</span>
                            </div>
                        )}
                        {selectedMetrics.total_participants && reportData.total_participants != null && (
                            <div className={styles.statCard}>
                                <span className={styles.statValue}>{reportData.total_participants?.toLocaleString('ar-SY')}</span>
                                <span className={styles.statLabel}>إجمالي المشاركين</span>
                            </div>
                        )}
                        {selectedMetrics.avg_participants && reportData.avg_participants != null && (
                            <div className={styles.statCard}>
                                <span className={styles.statValue}>{reportData.avg_participants?.toLocaleString('ar-SY')}</span>
                                <span className={styles.statLabel}>متوسط المشاركين</span>
                            </div>
                        )}
                    </div>
                )}

                {selectedMetrics.top_governorates && reportData.top_governorates?.length > 0 && (
                    <section className={styles.chartSection}>
                        <h2 className={styles.chartTitle}>أعلى المحافظات نشاطاً</h2>
                        <ChartRenderer
                            data={reportData.top_governorates}
                            type={chartTypes.top_governorates ?? 'bar'}
                        />
                    </section>
                )}

                {selectedMetrics.political_orientation && reportData.political_orientation?.length > 0 && (
                    <section className={styles.chartSection}>
                        <h2 className={styles.chartTitle}>التوجهات السياسية</h2>
                        <ChartRenderer
                            data={reportData.political_orientation}
                            type={chartTypes.political_orientation ?? 'pie'}
                            nameKey="orientation"
                        />
                    </section>
                )}

                {selectedMetrics.verified_ratio && reportData.verified_ratio && (
                    <section className={styles.chartSection}>
                        <h2 className={styles.chartTitle}>نسبة الاحتجاجات المرصودة ميدانياً</h2>
                        {(chartTypes.verified_ratio === 'pie' || chartTypes.verified_ratio === 'bar') ? (
                            <ChartRenderer
                                data={verifiedData}
                                type={chartTypes.verified_ratio}
                            />
                        ) : (
                            <div className={styles.progressWrap}>
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressFill}
                                        style={{ width: `${reportData.verified_ratio.percentage}%` }}
                                    />
                                </div>
                                <span className={styles.progressPct}>{reportData.verified_ratio.percentage}%</span>
                                <p className={styles.progressStats}>
                                    مرصود: {reportData.verified_ratio.verified?.toLocaleString('ar-SY')} &nbsp;|&nbsp;
                                    غير مرصود: {reportData.verified_ratio.unverified?.toLocaleString('ar-SY')}
                                </p>
                            </div>
                        )}
                    </section>
                )}

                {selectedMetrics.protest_type_distribution && reportData.protest_type_distribution?.length > 0 && (
                    <section className={styles.chartSection}>
                        <h2 className={styles.chartTitle}>توزيع أنواع الاحتجاجات</h2>
                        <ChartRenderer
                            data={reportData.protest_type_distribution}
                            type={chartTypes.protest_type_distribution ?? 'bar'}
                        />
                    </section>
                )}

                {selectedMetrics.tag_cloud && reportData.tag_cloud?.length > 0 && (
                    <section className={styles.chartSection}>
                        <h2 className={styles.chartTitle}>التصنيفات الفرعية الأكثر تكراراً</h2>
                        <ChartRenderer
                            data={reportData.tag_cloud}
                            type={chartTypes.tag_cloud ?? 'cloud'}
                        />
                    </section>
                )}
            </div>
        </div>
    );
}
